import React from 'react'
import { Avatar, Button } from '@mui/material'
import { UserStatus } from '@/utils/config/user-enums'
import { UserInterface } from '@/services/types/APIResponseType'
import { formatPhoneNumber, stringAvatar } from '@/utils'

interface TablePropsType {
  currentItems: UserInterface[]
  handleOnClickOpen: (item: UserInterface) => void
  handleApproval: (
    userId: string,
    approvalStatus: 'approved' | 'rejected',
  ) => void
}

const ApprovalCell: React.FC<{
  item: UserInterface
  onApproval: (userId: string, approvalStatus: 'approved' | 'rejected') => void
}> = ({ item, onApproval }) => {
  const status = item.approvalStatus

  if (status === 'approved') {
    return <label className="active">Approved</label>
  }

  if (status === 'rejected') {
    return <label className="block">Rejected</label>
  }

  return (
    <div className="flex gap-2">
      <Button
        size="small"
        onClick={() => onApproval(item._id, 'approved')}
        sx={{
          backgroundColor: 'rgba(52, 199, 89, 0.1)',
          color: 'rgba(52, 199, 89, 1)',
          borderRadius: '9999px',
          fontSize: '12px',
          padding: '4px 14px',
          textTransform: 'none',
          minWidth: 'auto',
          '&:hover': {
            backgroundColor: 'rgba(52, 199, 89, 0.25)',
          },
        }}
      >
        Accept
      </Button>
      <Button
        size="small"
        onClick={() => onApproval(item._id, 'rejected')}
        sx={{
          backgroundColor: 'rgba(199, 52, 52, 0.2)',
          color: 'rgb(253, 12, 4)',
          borderRadius: '9999px',
          fontSize: '12px',
          padding: '4px 14px',
          textTransform: 'none',
          minWidth: 'auto',
          '&:hover': {
            backgroundColor: 'rgba(199, 52, 52, 0.35)',
          },
        }}
      >
        Reject
      </Button>
    </div>
  )
}

const RegisteredBabysitterTable: React.FC<TablePropsType> = ({
  currentItems,
  handleOnClickOpen,
  handleApproval,
}) => {
  return (
    <div className="responsive-table-scroll">
      <table className="table-container bg-grad before:rounded-[20px] rounded-[20px]">
        <thead>
          <tr className="table-heading row-bg1">
            <th className="text-left whitespace-pre border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
              Name
            </th>
            <th className="text-left whitespace-pre border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
              Phone Number
            </th>
            <th className="text-left whitespace-pre border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
              Email Address
            </th>
            <th className="text-left whitespace-pre border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
              Status
            </th>
            <th className="text-left whitespace-pre border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
              Approval
            </th>
            <th className="text-left whitespace-pre border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
              Action
            </th>
          </tr>
        </thead>
        <tbody>
          {currentItems?.map((item, idx) => (
            <tr key={idx} className={idx % 2 === 0 ? 'rowbg2' : 'row-bg1'}>
              <td className="text-left border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                <div className="profile-cell">
                  {item.profileInfo?.profilePic ? (
                    <img
                      alt="Profile"
                      src={item.profileInfo?.profilePic}
                      className="consumer-avatar"
                    />
                  ) : (
                    <Avatar
                      className="!bg-[#365F91] mr-4"
                      {...stringAvatar(
                        `${item.profileInfo?.firstName} ${item.profileInfo?.lastName}`,
                      )}
                    />
                  )}
                  &nbsp;
                  <span className="flex-1 min-w-0 max-w-[160px] break-words whitespace-normal text-left">
                    {item?.profileInfo?.firstName}&nbsp;
                    {item?.profileInfo?.lastName}
                  </span>
                </div>
              </td>
              <td className="text-left border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                {formatPhoneNumber(
                  item.profileInfo?.phone,
                  item.profileInfo?.countryCode,
                )}
              </td>
              <td className="text-left border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                {item.email}
              </td>
              <td className="border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                <label
                  className={`${item.isAccountBlocked ? 'block' : 'active'}`}
                >
                  {!item.isAccountBlocked
                    ? UserStatus.ACTIVE
                    : UserStatus.BLOCK}
                </label>
              </td>
              <td className="border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                <ApprovalCell item={item} onApproval={handleApproval} />
              </td>
              <td className="border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                <Button onClick={() => handleOnClickOpen(item)}>
                  <img src="/icon/eye icon.png" alt="watch" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export { RegisteredBabysitterTable }
